"use client";
import {
  AppShell,
  Divider,
  NavLink,
  Paper,
  Skeleton,
} from "@mantine/core";
import { Subject } from "@tsTypes/subject-schema";
import { getMembershipIcon } from "lib/utils/meta";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { CSSProperties, useState } from "react";
import classes from "./AppContainer.module.css";
import Footer from "./footer/Footer";
import AboutLink from "./header/nav/main/AboutLinks";
import SubjectLinks from "./header/nav/main/SubjectLinks";
import AuthModal from "./header/nav/user/AuthModal";
import UserModal from "./header/nav/user/UserModal";
import Logo from "./logos/tiber/Logo";
import LogoLayout from "./logos/tiber/LogoLayout";
import LogoText from "./logos/tiber/LogoText";
import WatchDemoButton from "./WatchDemoButton";

export default function AppContainer({
  subjects,
  children,
}: {
  subjects: Subject[];
  children: React.ReactNode;
}) {
  const [opened, setOpened] = useState<boolean>(false);
  const { status, data: session } = useSession();
  const iconSize = 20;
  const membershipColor = getMembershipIcon(
    session?.user.role,
    session?.user.subscriptionMeta
  ).color;
  const navStyle = {
    "--profile-link-bg": `var(--mantine-color-${membershipColor}-5)`,
  } as CSSProperties;
  return (
    <AppShell
      classNames={{ main: classes.appShellMain }}
      padding={0}
      layout="alt"
      header={{ height: { base: 53, sm: 0 } }}
      navbar={{
        width: { base: "100vw", sm: "4rem" },
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
    >
      <AppShell.Header className={classes.headerMobileOnly} p={"sm"}>
        <LogoLayout opened={opened} setOpened={setOpened} />
      </AppShell.Header>
      <AppShell.Navbar className={classes.navBar} style={navStyle}>
        <div className={classes.logoSection}>
          <NavLink
            component={Link}
            href="/"
            label={
              <LogoText
                style={{
                  lineHeight: 1,
                  fontSize: "1.4rem",
                  marginTop: "0.3rem",
                }}
              />
            }
            leftSection={<Logo size={iconSize * 1.4} />}
            onClick={() => opened && setOpened(false)}
            variant="light"
            color="lightblue"
            classNames={{ root: classes.navLinkRoot }}
          />
          <Divider mt={"sm"} w={"100%"} />
        </div>
        <AppShell.Section>
          <AboutLink
            opened={opened}
            setOpened={setOpened}
            link={"#resources"}
            iconSize={iconSize}
          />
          <AboutLink
            opened={opened}
            setOpened={setOpened}
            link={"#reviews"}
            iconSize={iconSize}
          />
          <AboutLink
            opened={opened}
            setOpened={setOpened}
            link={"pricing"}
            iconSize={iconSize}
          />
          <AboutLink
            opened={opened}
            setOpened={setOpened}
            link={"team"}
            iconSize={iconSize}
          />
          <AboutLink
            opened={opened}
            setOpened={setOpened}
            link={"blog"}
            iconSize={iconSize}
          />
          <AboutLink
            opened={opened}
            setOpened={setOpened}
            link={"about"}
            iconSize={iconSize}
          />
        </AppShell.Section>
        <Divider />
        <AppShell.Section grow>
          {subjects.map((subject) => (
            <SubjectLinks
              key={subject.documentId}
              opened={opened}
              setOpened={setOpened}
              subjectName={subject.Name}
            />
          ))}
        </AppShell.Section>
        <AppShell.Section>
          <WatchDemoButton isNavLink={true} />
        </AppShell.Section>
        <Divider mb={"sm"} />
        <AppShell.Section>
          {status === "loading" ? (
            <Skeleton h={87} animate />
          ) : session?.user ? (
            <UserModal
              userMeta={session.user}
              opened={opened}
              setOpened={setOpened}
            />
          ) : (
            <AuthModal
              opened={opened}
              setOpened={setOpened}
              iconSize={iconSize}
            />
          )}
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main>
        <Paper className={classes.contentWrapper} radius={0}>
          {children}
          <Footer liveSubjectNames={subjects.map((subject) => subject.Name)} />
        </Paper>
      </AppShell.Main>
    </AppShell>
  );
}
